import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Sidebar from './components/Sidebar';
import LoadingScreen from './components/LoadingScreen';
import ErrorScreen from './components/ErrorScreen';
import Overview from './pages/Overview';
import Groups from './pages/Groups';
import Users from './pages/Users';
import Settings from './pages/Settings';
import Upload from './pages/Upload';
import { DateRangeProvider } from './contexts/DateRangeContext';
import { AuthProvider } from './contexts/AuthContext';
import AuthGuard from './components/AuthGuard';
import { initializeData, getDataLoadStatus } from './utils/dataProcessor';
import { useQueryParams } from './hooks/useQueryParams';

// React Query 클라이언트 생성
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5분
      gcTime: 10 * 60 * 1000, // 10분 
    },
  },
});

function App() {
  const { getQueryParam, setQueryParam } = useQueryParams();
  const [activeTab, setActiveTab] = useState(() => {
    // URL에서 초기 페이지 읽기
    const pageParam = getQueryParam('page');
    const validPages = ['overview', 'insights', 'users', 'groups', 'providers', 'reports', 'upload', 'settings'];
    return validPages.includes(pageParam || '') ? pageParam! : 'groups';
  });
  const [appState, setAppState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);

  // 탭 변경 시 URL 업데이트
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setQueryParam('page', tab);
  };

  useEffect(() => {
    console.log('🚀 App initialization started');
    
    const initializeApp = async () => {
      try {
        // 데이터 초기화
        setAppState('loading');
        console.log('🚀 Initializing app data...');
        
        await initializeData();
        
        const status = getDataLoadStatus();
        if (status.isLoaded) {
          setAppState('ready');
          console.log('✅ App data initialization complete:', status.dataCount, 'items');
          if (status.error) {
            console.warn('⚠️ API load failed, but continuing with local data:', status.error);
          }
        } else {
          throw new Error('Data load failed');
        }
      } catch (error) {
        console.error('❌ App initialization failed:', error);
        setLoadError(error instanceof Error ? error.message : 'Unknown error');
        setAppState('error');
      }
    };
    
    initializeApp();
  }, []);

  // 로딩 상태 UI
  if (appState === 'loading') {
    return <LoadingScreen />;
  }

  // 에러 상태 UI
  if (appState === 'error') {
    return (
      <LoadingScreen />
      // <ErrorScreen 
      //   error={loadError} 
      //   onRetry={() => {
      //     setAppState('loading');
      //     setLoadError(null);
      //     window.location.reload();
      //   }} 
      // />
    );
  }

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return <Overview />;
      case 'insights':
        return <div className="p-8"><h1>인사이트 페이지 - 곧 공개</h1></div>;
      case 'users':
        return <Users onTabChange={handleTabChange} />;
      case 'groups':
        return <Groups onTabChange={handleTabChange} />;
      case 'providers':
        return <div className="p-8"><h1>프로바이더 페이지 - 곧 공개</h1></div>;
      case 'reports':
        return <div className="p-8"><h1>리포트 페이지 - 곧 공개</h1></div>;
      case 'upload':
        return <Upload />;
      case 'settings':
        return <Settings />;
      default:
        return <Overview />;
    }
  };

  // 메인 앱 렌더링 (데이터 로드 완료)
  return (
    <AuthProvider>
      <AuthGuard>
        <QueryClientProvider client={queryClient}>
          <DateRangeProvider>
            <div className="flex h-screen bg-gray-50">
              <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />
              <main className="flex-1 overflow-auto p-8">
                {renderContent()}
              </main>
            </div>
          </DateRangeProvider>
        </QueryClientProvider>
      </AuthGuard>
    </AuthProvider>
  );
}

export default App;
